/**
 * Storage Service - Supabase Storage helpers
 * Replaces AWS S3 Storage operations
 */

import { supabase, STORAGE_BUCKETS as BUCKETS, getPublicUrl } from './supabase';

// Re-export for convenience
export const STORAGE_BUCKETS = BUCKETS;

// URL cache for performance
const urlCache = new Map();
const URL_CACHE_DURATION = 14 * 60 * 1000; // 14 minutes

/**
 * Get the appropriate size key for an image
 * @param {string} baseKey - Base image key (e.g., "userId-timestamp.jpg")
 * @param {string} size - Size variant: 'thumbnail', 'medium', or 'large'
 * @returns {string} The full key with size suffix
 */
export const getImageKey = (baseKey, size = 'medium') => {
  if (!baseKey) return null;
  
  // Remove .jpg extension to get base
  const base = baseKey.replace(/\.jpg$/, '');
  
  switch (size) {
    case 'thumbnail':
      return `${base}-thumbnail.jpg`;
    case 'medium':
      return `${base}-medium.jpg`;
    case 'large':
    default:
      return `${base}.jpg`;
  }
};

/**
 * Get a public URL for a profile picture
 * @param {string} profilePictureKey - The storage key from the user record
 * @param {string} size - Size variant: 'thumbnail', 'medium', or 'large'
 * @returns {string|null} Public URL or null if no key
 */
export const getProfilePictureUrl = (profilePictureKey, size = 'medium') => {
  if (!profilePictureKey) return null;
  
  // If it's already a full URL, return it
  if (profilePictureKey.startsWith('http')) {
    return profilePictureKey;
  }
  
  // Check cache
  const cacheKey = `${profilePictureKey}:${size}`;
  const cached = urlCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < URL_CACHE_DURATION) {
    return cached.url;
  }
  
  // Generate URL
  const imageKey = getImageKey(profilePictureKey, size);
  const url = getPublicUrl(STORAGE_BUCKETS.PROFILE_PICTURES, imageKey);
  
  // Cache the URL
  urlCache.set(cacheKey, { url, timestamp: Date.now() });
  
  return url;
};

/**
 * Get a public URL for an event image
 * @param {string} imageKey - The storage key from the event record
 * @returns {string|null} Public URL or null if no key
 */
export const getEventImageUrl = (imageKey) => {
  if (!imageKey) return null;
  
  if (imageKey.startsWith('http')) {
    return imageKey;
  }
  
  return getPublicUrl(STORAGE_BUCKETS.EVENT_IMAGES, imageKey);
};

/**
 * Get a public URL for a hotspot photo
 * @param {string} photoKey - The storage key
 * @returns {string|null} Public URL or null if no key
 */
export const getHotspotPhotoUrl = (photoKey) => {
  if (!photoKey) return null;
  
  if (photoKey.startsWith('http')) {
    return photoKey;
  }
  
  return getPublicUrl(STORAGE_BUCKETS.HOTSPOT_PHOTOS, photoKey);
};

/**
 * Upload a file to a bucket
 * @param {string} bucket - Bucket name
 * @param {string} path - File path within bucket
 * @param {Blob|File} file - The file to upload
 * @param {object} options - Upload options
 * @returns {Promise<{data, error}>}
 */
export const uploadFile = async (bucket, path, file, options = {}) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: true,
      ...options,
    });
  
  return { data, error };
};

/**
 * Delete a file from a bucket
 * @param {string} bucket - Bucket name
 * @param {string} path - File path within bucket
 * @returns {Promise<{data, error}>}
 */
export const deleteFile = async (bucket, path) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .remove([path]);
  
  return { data, error };
};

/**
 * Upload event image
 * @param {string} eventId - Event ID
 * @param {Blob|File} file - Image file
 * @returns {Promise<{key, url, error}>}
 */
export const uploadEventImage = async (eventId, file) => {
  const key = `${eventId}-${Date.now()}.jpg`;
  const { error } = await uploadFile(STORAGE_BUCKETS.EVENT_IMAGES, key, file, {
    contentType: 'image/jpeg',
  });
  
  if (error) {
    return { error };
  }
  
  const url = getEventImageUrl(key);
  return { key, url };
};

/**
 * Upload hotspot photo
 * @param {string} hotspotId - Hotspot ID
 * @param {Blob|File} file - Image file
 * @returns {Promise<{key, url, error}>}
 */
export const uploadHotspotPhoto = async (hotspotId, file) => {
  const key = `${hotspotId}-${Date.now()}.jpg`;
  const { error } = await uploadFile(STORAGE_BUCKETS.HOTSPOT_PHOTOS, key, file, {
    contentType: 'image/jpeg',
  });
  
  if (error) {
    return { error };
  }
  
  const url = getHotspotPhotoUrl(key);
  return { key, url };
};

export default {
  getImageKey,
  getProfilePictureUrl,
  getEventImageUrl,
  getHotspotPhotoUrl,
  uploadFile,
  deleteFile,
  uploadEventImage,
  uploadHotspotPhoto,
  STORAGE_BUCKETS,
};

